import React, { useEffect, useRef, useState } from 'react';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import 'handsontable/dist/handsontable.full.min.css';
import { db } from '../firebaseConfig';
import { doc, getDoc, setDoc, getDocs, collection, deleteDoc } from 'firebase/firestore';
import FullScreenButton from './FullScreenButton';

const MIN_ROWS = 10;

const HEADERS = ['차량번호', '차종', '이름', '연락처', '사업자번호', '소속', '비고', '메모'];
const FIELDS = ['vehicleNumber', 'cargo', 'driverName', 'contact', 'businessNumber', 'department', 'remarks', 'memo'];

interface VehicleRow {
  vehicleNumber: string;
  cargo: string;
  driverName: string;
  contact: string;
  businessNumber: string;
  department: string;
  remarks: string;
  memo: string;
}

interface VehicleInfoPageProps {
  isFullScreen: boolean;
  setIsFullScreen: (v: boolean) => void;
}

const VehicleInfoPage: React.FC<VehicleInfoPageProps> = ({ isFullScreen, setIsFullScreen }) => {
  const [tableData, setTableData] = useState<any[][]>([HEADERS]);
  const hotRef = useRef<any>(null);

  // Firestore에서 차량정보 목록 불러오기
  useEffect(() => {
    const fetchVehicles = async () => {
      const colRef = collection(db, 'dispatch_vehicleNumber');
      const querySnap = await getDocs(colRef);
      const vehicles: VehicleRow[] = [];
      querySnap.forEach(doc => {
        vehicles.push(doc.data() as VehicleRow);
      });
      // 데이터만 rows에 저장 (헤더 없음)
      const rows = vehicles.map((v: VehicleRow) => FIELDS.map(f => v[f as keyof VehicleRow] || ''));
      while (rows.length < MIN_ROWS) {
        rows.push(Array(HEADERS.length).fill(''));
      }
      setTableData(rows);
    };
    fetchVehicles();
  }, []);

  // Firestore에 vehicleList 배열 저장 → 각 차량을 문서로 저장
  const saveVehicle = async (vehicle: VehicleRow) => {
    if (!vehicle.vehicleNumber) return;
    await setDoc(doc(db, 'dispatch_vehicleNumber', vehicle.vehicleNumber), vehicle);
  };

  // 차량번호 중복 체크 함수
  const isVehicleNumberDuplicate = async (vehicleNumber: string) => {
    if (!vehicleNumber) return false;
    const docRef = doc(db, 'dispatch_vehicleNumber', vehicleNumber);
    const docSnap = await getDoc(docRef);
    return docSnap.exists();
  };

  // 셀 수정 시 Firestore에 저장 (중복 체크 포함)
  const handleAfterChange = async (changes: any, source: string) => {
    if (source === 'loadData' || !changes) return;
    let newTable = hotRef.current?.hotInstance?.getData() || tableData;
    for (const [row, col, oldValue, newValue] of changes) {
      const vehicleNumber = (newTable[row][0] || '').trim();
      if (!vehicleNumber) continue;
      // 차량번호가 바뀌었거나 새로 입력된 경우만 중복 체크
      if (col === 0 && newValue !== oldValue) {
        const isDuplicate = await isVehicleNumberDuplicate(vehicleNumber);
        if (isDuplicate) {
          alert('이미 등록된 차량입니다');
          newTable[row][0] = '';
          setTableData([...newTable]);
          return;
        }
      }
      // 저장
      const vehicle: VehicleRow = {
        vehicleNumber,
        cargo: newTable[row][1] || '',
        driverName: newTable[row][2] || '',
        contact: newTable[row][3] || '',
        businessNumber: newTable[row][4] || '',
        department: newTable[row][5] || '',
        remarks: newTable[row][6] || '',
        memo: newTable[row][7] || '',
      };
      await saveVehicle(vehicle);
    }
  };

  // 행 삭제 함수 (차량번호로 삭제)
  const handleDeleteRow = async (rowIndex: number) => {
    const row = tableData[rowIndex];
    const vehicleNumber = row[0];
    if (vehicleNumber) {
      await deleteDoc(doc(db, 'dispatch_vehicleNumber', vehicleNumber));
    }
    setTableData(prev => prev.filter((_, idx) => idx !== rowIndex));
  };

  const handleAddRow = () => {
    setTableData((prev) => [...prev, Array(HEADERS.length).fill('')]);
    setTimeout(() => {
      if (hotRef.current) {
        const row = tableData.length;
        hotRef.current.hotInstance.selectCell(row, 0);
      }
    }, 100);
  };

  return (
    <div
      tabIndex={0}
      onClick={() => {
        if (hotRef.current) {
          hotRef.current.hotInstance.listen();
        }
      }}
      style={{ width: '100%', maxWidth: 1100, margin: '0 auto', padding: 32, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 48px)', overflow: 'hidden', boxSizing: 'border-box' }}
    >
      {/* 헤더 영역 */}
      <div style={{ flex: '0 0 auto' }}>
        <h2 style={{ textAlign: 'center', marginBottom: 16, color: '#1976d2' }}>차량정보 관리</h2>
        <p style={{ color: '#888', fontSize: 14, marginBottom: 8 }}>
          엑셀/구글시트에서 차량정보 목록을 복사해 <b>셀을 한 번 클릭(커서 없이 파란 테두리만 보이는 상태)</b> 후 <b>Ctrl+V</b>로 붙여넣기 하세요.<br/>
          셀을 더블클릭(커서 깜빡임) 상태에서는 여러 행/열 붙여넣기가 안 됩니다.
        </p>
        <button onClick={handleAddRow} style={{ marginBottom: 12, padding: '6px 18px', borderRadius: 4, border: 'none', background: '#1976d2', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>+ 추가</button>
      </div>

      {/* 표 영역: 내부 스크롤 */}
      <div style={{ flex: '1 1 0', minHeight: 0, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
        <HotTable
          ref={hotRef}
          data={tableData}
          colHeaders={HEADERS}
          rowHeaders={true}
          width="100%"
          height="100%"
          licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY || 'non-commercial-and-evaluation'}
          manualRowResize={true}
          manualColumnResize={true}
          autoColumnSize={true}
          autoRowSize={false}
          className="htCenter"
          afterChange={handleAfterChange}
          readOnly={false}
          contextMenu={{
            items: {
              'remove_row': {
                name: '삭제',
                callback: function(key: string, selection: any) {
                  const rowIndex = selection[0].start.row;
                  handleDeleteRow(rowIndex);
                }
              }
            }
          }}
          copyPaste={true}
          beforePaste={() => {
            if (hotRef.current) hotRef.current.hotInstance.listen();
          }}
          stretchH="all"
        />
      </div>

      {/* 오른쪽 상단 전체화면/원래대로 버튼 */}
      <FullScreenButton isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />
    </div>
  );
};

export default VehicleInfoPage; 